import { useCallback } from 'react'

import { groupSearchResults } from './helpers'
import type { ConfigurationItemSearchFunctions } from './types'
import { useEnumSearch } from './useEnumSearch'
import { useGlMapSearch } from './useGlMapSearch'
import { useParameterSearch } from './useParameterSearch'
import { useReconciliationSpecSearch } from './useReconciliationSpecSearch'
import { useRegisteredTenantsSearch } from './useRegisteredTenantsSearch'
import { useTemplateSearch } from './useTemplateSearch'

export const useSearchOperations = (): ConfigurationItemSearchFunctions => {
  const {
    name: templatesByName,
    className: templatesByClass,
    fplus: templatesByFPlus,
    properties: templatesByProperties,
    all: allTemplates,
  } = useTemplateSearch()
  const {
    name: parameterDefinitionsByName,
    fplus: parameterDefinitionsByFPlus,
    properties: parameterDefinitionsByProperties,
    all: allParameters,
  } = useParameterSearch()
  const {
    name: enumsByName,
    className: enumsByClass,
    properties: enumsByProperties,
    all: allEnums,
  } = useEnumSearch()
  const { name: tenantsByName, all: allTenants } = useRegisteredTenantsSearch()
  const { name: glMapsByName, all: allGlMaps } = useGlMapSearch()
  const { name: reconciliationSpecsByName, all: allReconciliationSpecs } =
    useReconciliationSpecSearch()

  const searchByName = useCallback(
    async (query: string) => {
      const templateMatches = await templatesByName(query)
      const parameterMatches = await parameterDefinitionsByName(query)
      const enumMatches = await enumsByName(query)
      const tenantMatches = await tenantsByName(query)
      const glMapMatches = await glMapsByName(query)
      const reconciliationSpecMatches = await reconciliationSpecsByName(query)

      return groupSearchResults(
        ...templateMatches,
        ...parameterMatches,
        ...enumMatches,
        ...tenantMatches,
        ...glMapMatches,
        ...reconciliationSpecMatches,
      )
    },
    [
      enumsByName,
      parameterDefinitionsByName,
      templatesByName,
      tenantsByName,
      glMapsByName,
      reconciliationSpecsByName,
    ],
  )

  const searchByClass = useCallback(
    async (query: string) => {
      const templateMatches = await templatesByClass(query)
      const enumMatches = await enumsByClass(query)

      return groupSearchResults(...templateMatches, ...enumMatches)
    },
    [enumsByClass, templatesByClass],
  )

  const searchByProperties = useCallback(
    async (query: string) => {
      const templateMatches = await templatesByProperties(query)
      const enumMatches = await enumsByProperties(query)
      const parameterDefinitionMatches =
        await parameterDefinitionsByProperties(query)

      return groupSearchResults(
        ...templateMatches,
        ...enumMatches,
        ...parameterDefinitionMatches,
      )
    },
    [
      enumsByProperties,
      templatesByProperties,
      parameterDefinitionsByProperties,
    ],
  )

  const searchByFPlus = useCallback(
    async (query: string) => {
      const templateMatches = await templatesByFPlus(query)
      const parameterDefinitionMatches =
        await parameterDefinitionsByFPlus(query)

      return groupSearchResults(
        ...templateMatches,
        ...parameterDefinitionMatches,
      )
    },
    [parameterDefinitionsByFPlus, templatesByFPlus],
  )

  const searchAll = useCallback(
    async (query: string) => {
      const templateMatches = await allTemplates(query)
      const parameterMatches = await allParameters(query)
      const enumMatches = await allEnums(query)
      const tenantMatches = await allTenants(query)
      const glMapMatches = await allGlMaps(query)
      const reconciliationSpecMatches = await allReconciliationSpecs(query)

      return groupSearchResults(
        ...templateMatches,
        ...parameterMatches,
        ...enumMatches,
        ...tenantMatches,
        ...glMapMatches,
        ...reconciliationSpecMatches,
      )
    },
    [
      allEnums,
      allParameters,
      allTemplates,
      allTenants,
      allGlMaps,
      allReconciliationSpecs,
    ],
  )

  return {
    name: searchByName,
    className: searchByClass,
    fplus: searchByFPlus,
    properties: searchByProperties,
    all: searchAll,
  }
}
